import { zodResolver } from '@hookform/resolvers/zod'
import { cn } from '@repo/tailwind-config/tailwind.functions'
import { useQuery } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { z, ZodType } from 'zod'

import { CenterTeachingHallInput } from '../../../../../api/centers/center-teaching-hall/assign-center-teaching-hall'
import { getCenterTeachingHalls } from '../../../../../api/centers/center-teaching-hall/get-center-teaching-hall'
import { getTeachingHalls } from '../../../../../api/ols/teaching-halls/get-teaching-halls'
import { BaseFormProps } from '../../../../../lib/types/utils'
import { getSubmitAction } from '../../../../../lib/utils/buttons-config/validation-buttons-config'
import { getSelectedOption } from '../../../../../lib/utils/functions/misc.functions'
import { createNumberSchema } from '../../../../../lib/utils/validations/int-array-validation'
import { FORM_MODE } from '../../../../../utils/constants/form.constants'
import { Form, FormControl, FormField, FormItem } from '../../../../elements/forms/form'
import ControlledSelectInput from '../../../../elements/inputs/controlled-select-input'
import ValidationButtons from '../../../../elements/validation-buttons'

type Props = BaseFormProps<CenterTeachingHallInput, true> & {
  className?: string
  olId?: number
  centerId: number
}

const formSchema: ZodType<CenterTeachingHallInput> = z.object({
  teachingHallId: createNumberSchema('Teaching Hall')
})

const CenterTeachingHallForm = ({
  className,
  olId,
  centerId,
  defaultValues,
  ...validationButtonProps
}: Props) => {
  const form = useForm<CenterTeachingHallInput>({
    resolver: zodResolver(formSchema),
    defaultValues,
    mode: FORM_MODE
  })

  const { data: centerTeachingHalls } = useQuery({
    queryKey: ['center-teaching-halls', centerId],
    queryFn: () => getCenterTeachingHalls(centerId)
  })

  const { data: teachingHalls } = useQuery({
    queryKey: ['teaching-halls'],
    queryFn: () => {
      if (!olId) return
      return getTeachingHalls(olId)
    },
    enabled: !!olId
  })

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(getSubmitAction(validationButtonProps.buttonsConfig))}>
        <div className={cn('flex flex-col ', className)}>
          <FormField
            control={form.control}
            name={'teachingHallId'}
            render={({ field }) => {
              const selectedTeachingHall = teachingHalls?.data.find((th) => th.id === field.value)
              return (
                <FormItem className={'flex-1'}>
                  <FormControl>
                    <ControlledSelectInput
                      {...field}
                      required
                      dropDownPosition="above"
                      label={'Teaching hall'}
                      options={teachingHalls?.data
                        .filter((th) => !centerTeachingHalls?.data.some((cth) => cth.id === th.id))
                        .map((th) => ({ label: th.name, value: th.id }))}
                      selectedOption={getSelectedOption(
                        field.value,
                        selectedTeachingHall ? selectedTeachingHall.name : undefined
                      )}
                    />
                  </FormControl>
                </FormItem>
              )
            }}
          />
        </div>

        <ValidationButtons {...validationButtonProps} hasBorderTop />
      </form>
    </Form>
  )
}

export default CenterTeachingHallForm
